import { cn } from '@/lib/utils';
import { formatoCantidad, formatoFecha, formatoHora } from '@/lib/utils/formato-precio';
import { RESULTADOS, cuantasDe, cuantasPropuso } from '../../lib/corridas';
import type { EventoDeLaLinea } from '../../types/corridas.types';
import { resumenDeLaColumna } from './tira-de-corridas';

/** Cuántas columnas como mucho (las más nuevas del período). */
export const COLUMNAS_DEL_GRAFICO = 50;

/**
 * «Cómo se comportó fecha a fecha» (maqueta: `chartHTML`): una columna por
 * corrida, de la más vieja a la más nueva; la altura es cuántas propuso, apilada
 * por lo que pasó; un punto si una regla limitó alguna; «R» en los recálculos
 * (también en los que no propusieron nada). Con `onElegir`, toda columna se
 * puede tocar (ronda de arreglos, revisión T10/T11): la vista resalta su corrida
 * y, si propuso algo, abre sus productos.
 */
export function GraficoDeCorridas({
  eventos,
  onElegir,
}: {
  eventos: EventoDeLaLinea[];
  onElegir?: (e: EventoDeLaLinea) => void;
}) {
  const columnas = eventos.slice(0, COLUMNAS_DEL_GRAFICO).reverse();
  if (columnas.length === 0)
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">Sin corridas en el período.</p>
    );
  const maximo = Math.max(1, ...columnas.map(cuantasPropuso));
  const conHora = columnas.length <= 20;
  return (
    <div data-testid="grafico-de-corridas" className="overflow-hidden">
      <div className="flex gap-2">
        {/* Ronda final de R4 (QA E8): margen arriba (el mismo en el eje) para la
            «R» y el punto de la columna más alta, que antes quedaban cortados. */}
        <div
          data-testid="eje-del-grafico"
          className="flex h-48 w-8 shrink-0 flex-col justify-between pt-6 text-right text-xs tabular-nums text-muted-foreground"
        >
          <span>{formatoCantidad(maximo)}</span>
          <span>{formatoCantidad(Math.round(maximo / 2))}</span>
          <span>0</span>
        </div>
        <div
          data-testid="columnas-del-grafico"
          className="flex h-48 min-w-0 flex-1 items-end gap-1 border-b border-l px-1 pt-6"
        >
          {columnas.map((e) => {
            const n = cuantasPropuso(e);
            // Lo que propuso y después reescribió otro (sin color de resultado).
            const reescritas = n - e.conteos.propuestas;
            const noTermino = e.estado === 'FALLO' || e.estado === 'PERDIDA';
            const titulo = resumenDeLaColumna(e);
            const contenido = (
              <span className="flex h-full w-full max-w-[34px] flex-col-reverse items-center">
                {e.estado === 'EN_CURSO' ? (
                  <span className="mb-0.5 h-6 w-full rounded-sm border border-dashed border-primary" />
                ) : noTermino ? (
                  <span className="mb-0.5 text-xs font-bold text-bloqueo">×</span>
                ) : n === 0 ? (
                  <>
                    <span className="mb-0.5 h-1.5 w-1.5 rounded-full bg-muted-foreground/50" />
                    {e.tipo === 'RECALCULO' && (
                      <span className="shrink-0 text-xs font-bold leading-none">R</span>
                    )}
                  </>
                ) : (
                  <>
                    {RESULTADOS.map((r) => {
                      const k = cuantasDe(e.conteos, r.clave);
                      return k > 0 ? (
                        <i
                          key={r.clave}
                          className={cn('block w-full shrink-0', r.fondo)}
                          style={{ height: `${(k / maximo) * 100}%` }}
                        />
                      ) : null;
                    })}
                    {reescritas > 0 && (
                      <i
                        data-testid="reescritas-de-la-columna"
                        className="block w-full shrink-0 bg-muted-foreground/30"
                        style={{ height: `${(reescritas / maximo) * 100}%` }}
                      />
                    )}
                    {/* Ronda del QA de R4 (R4-39): en la columna más alta las
                        barras llenan el 100 %; sin `shrink-0` el punto se
                        aplastaba a 0 px. Sube al margen de arriba (`pt-6`). */}
                    {e.conteos.limitadas > 0 && (
                      <span
                        data-testid="punto-limitada"
                        className="mb-0.5 h-2 w-2 shrink-0 rounded-full bg-baja"
                      />
                    )}
                    {e.tipo === 'RECALCULO' && (
                      <span className="shrink-0 text-xs font-bold leading-none">R</span>
                    )}
                  </>
                )}
              </span>
            );
            return (
              <div key={e.id} className="flex h-full min-w-0 flex-1 justify-center">
                {onElegir ? (
                  <button
                    type="button"
                    title={titulo}
                    aria-label={titulo}
                    onClick={() => onElegir(e)}
                    className="flex h-full w-full justify-center rounded-sm hover:bg-muted/50"
                  >
                    {contenido}
                  </button>
                ) : (
                  <span
                    title={titulo}
                    aria-label={titulo}
                    className="flex h-full w-full justify-center"
                  >
                    {contenido}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div className="ml-10 flex gap-1 px-1 pt-1">
        {columnas.map((e, i) => {
          const dia = formatoFecha(e.cuando);
          const nuevoDia = i === 0 || formatoFecha(columnas[i - 1].cuando) !== dia;
          return (
            <div key={e.id} className="min-w-0 flex-1 text-center text-xs text-muted-foreground">
              <span className="block truncate">{nuevoDia ? dia : ' '}</span>
              {conHora && (
                <span className="block truncate opacity-70">{formatoHora(e.cuando)}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
